/**
 * Reinstall, Disable, and Enable for an infra plugin (Feature 84, D8, D23).
 *
 * The platform installs an infra plugin itself at every boot, so the
 * catalog's install and uninstall refuse it. What an operator can do instead
 * is run that boot install now (Reinstall), or keep the plugin off across
 * boots (Disable) and bring it back (Enable). Reinstall answers with an
 * install job, followed through the active-jobs poll like any install.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@optum/api-client/client';
import { disablePlugin, enablePlugin, reinstallPlugin } from '@optum/api-client/endpoints/plugins';

import { qk } from '../../lib/query-keys.js';
import { useToast } from '../../ui/useToast.js';

function message(err: unknown): string {
  return err instanceof ApiError ? err.message : String(err);
}

/** The three actions for the plugin named `name`; the name is all they need. */
export function useInfraPluginActions(name: string) {
  const qc = useQueryClient();
  const toast = useToast();

  /** The entry, the catalog, and the active jobs: everything an action changes. */
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['catalog'] });
    void qc.invalidateQueries({ queryKey: qk.installJobsActive() });
  };

  const reinstall = useMutation({
    mutationFn: () => reinstallPlugin(name),
    onSuccess: (job) => {
      toast.success(`Reinstalling ${name}`, `Version ${job.version} is installing.`);
      refresh();
    },
    onError: (err) => toast.error('Reinstall failed', message(err)),
  });

  const disable = useMutation({
    mutationFn: () => disablePlugin(name),
    onSuccess: () => {
      toast.success(`Disabled ${name}`, 'Its server stopped; boots keep it disabled.');
      refresh();
    },
    onError: (err) => toast.error('Disable failed', message(err)),
  });

  const enable = useMutation({
    mutationFn: () => enablePlugin(name),
    onSuccess: (status) => {
      toast.success(
        `Enabled ${name}`,
        status.reinstall_job_id ? 'Its directory was gone, so it is reinstalling.' : undefined,
      );
      refresh();
    },
    onError: (err) => toast.error('Enable failed', message(err)),
  });

  return { reinstall, disable, enable };
}
